// ULTIMATE VERSION: Enhanced Audio Recording Utility with Level Visualization

// Configuration for audio recording and silence detection
export interface AudioRecorderConfig {
	silenceThreshold: number // 0-255, default 5
	silenceDuration: number // ms before triggering silence callback
	smoothingTimeConstant: number // 0-1, for audio level smoothing
	fftSize: 256 | 512 | 1024 | 2048 | 4096 | 8192 // FFT size for analysis
}

const DEFAULT_CONFIG: AudioRecorderConfig = {
	silenceThreshold: 5,
	silenceDuration: 4000,
	smoothingTimeConstant: 0.8,
	fftSize: 2048,
}

export class AudioRecorder {
	private mediaRecorder: MediaRecorder | null = null
	private audioChunks: Blob[] = []
	private stream: MediaStream | null = null
	private audioContext: AudioContext | null = null
	private analyser: AnalyserNode | null = null
	private silenceTimer: number | null = null
	private onSilenceCallback: (() => void) | null = null
	private isSilenceDetectionActive: boolean = false

	// ULTIMATE: Audio level visualization support
	private currentAudioLevel: number = 0
	private peakAudioLevel: number = 0
	private audioLevelHistory: number[] = []
	private readonly LEVEL_HISTORY_SIZE = 10
	private config: AudioRecorderConfig
	private dataArray: Uint8Array<ArrayBuffer> | null = null
	private isAnalyserSetup: boolean = false

	constructor(config: Partial<AudioRecorderConfig> = {}) {
		this.config = { ...DEFAULT_CONFIG, ...config }
	}

	async startRecording(): Promise<boolean> {
		try {
			// Request microphone access
			this.stream = await navigator.mediaDevices.getUserMedia({
				audio: {
					echoCancellation: true,
					noiseSuppression: true,
					autoGainControl: true,
				},
			})

			// Create MediaRecorder with webm format (best browser support)
			const mimeType = MediaRecorder.isTypeSupported('audio/webm')
				? 'audio/webm'
				: 'audio/ogg'

			this.mediaRecorder = new MediaRecorder(this.stream, {
				mimeType,
			})

			this.audioChunks = []

			this.mediaRecorder.ondataavailable = (event) => {
				if (event.data.size > 0) {
					this.audioChunks.push(event.data)
				}
			}

			this.mediaRecorder.start()

			// ULTIMATE: Always set up audio analyser for level visualization
			this.setupAudioAnalyser()

			// Set up silence detection monitoring if explicitly enabled
			if (this.isSilenceDetectionActive) {
				this.startAudioMonitoring()
			}

			return true
		} catch (error) {
			console.error('Failed to start recording:', error)
			return false
		}
	}

	// ULTIMATE: Set up audio analyser for both level visualization and silence detection
	private setupAudioAnalyser() {
		if (!this.stream || this.isAnalyserSetup) return

		// Create audio context and analyser
		this.audioContext = new AudioContext()
		this.analyser = this.audioContext.createAnalyser()
		this.analyser.fftSize = this.config.fftSize
		this.analyser.smoothingTimeConstant = this.config.smoothingTimeConstant

		// Connect microphone to analyser
		const source = this.audioContext.createMediaStreamSource(this.stream)
		source.connect(this.analyser)

		// Pre-allocate data array for performance
		this.dataArray = new Uint8Array(this.analyser.frequencyBinCount)
		this.isAnalyserSetup = true

		// Start continuous level monitoring for visualization
		this.startLevelMonitoring()
	}

	// ULTIMATE: Continuous audio level monitoring for UI visualization
	private startLevelMonitoring() {
		const updateLevel = () => {
			if (!this.analyser || !this.dataArray || !this.isRecording()) return

			this.analyser.getByteFrequencyData(this.dataArray)

			// Calculate RMS (Root Mean Square) for more accurate level
			let sum = 0
			for (let i = 0; i < this.dataArray.length; i++) {
				sum += this.dataArray[i]! * this.dataArray[i]!
			}
			const rms = Math.sqrt(sum / this.dataArray.length)

			// Normalize to 0-1 range (255 is max byte value)
			const normalizedLevel = Math.min(rms / 128, 1)

			// Update current level with smoothing
			this.currentAudioLevel =
				this.currentAudioLevel * 0.7 + normalizedLevel * 0.3

			// Track peak level with decay
			if (normalizedLevel > this.peakAudioLevel) {
				this.peakAudioLevel = normalizedLevel
			} else {
				this.peakAudioLevel *= 0.995 // Slow decay
			}

			// Keep history for waveform visualization
			this.audioLevelHistory.push(this.currentAudioLevel)
			if (this.audioLevelHistory.length > this.LEVEL_HISTORY_SIZE) {
				this.audioLevelHistory.shift()
			}

			// Continue monitoring while recording
			if (this.isRecording()) {
				requestAnimationFrame(updateLevel)
			}
		}

		requestAnimationFrame(updateLevel)
	}

	// ULTIMATE: Start audio monitoring for silence detection (uses shared analyser)
	private startAudioMonitoring() {
		if (!this.analyser || !this.dataArray) return

		const checkSilence = () => {
			if (!this.analyser || !this.dataArray || !this.isSilenceDetectionActive)
				return

			this.analyser.getByteFrequencyData(this.dataArray)

			// Calculate average volume
			const average =
				this.dataArray.reduce((sum, value) => sum + value, 0) /
				this.dataArray.length

			if (average < this.config.silenceThreshold) {
				// Start or continue silence timer
				if (!this.silenceTimer) {
					this.silenceTimer = window.setTimeout(() => {
						if (this.onSilenceCallback && this.isRecording()) {
							this.onSilenceCallback()
						}
					}, this.config.silenceDuration)
				}
			} else {
				// Reset silence timer if sound detected
				if (this.silenceTimer) {
					clearTimeout(this.silenceTimer)
					this.silenceTimer = null
				}
			}

			// Continue monitoring
			if (this.isSilenceDetectionActive && this.isRecording()) {
				requestAnimationFrame(checkSilence)
			}
		}

		checkSilence()
	}

	setSilenceCallback(callback: () => void) {
		this.onSilenceCallback = callback
	}

	enableSilenceDetection(enable: boolean) {
		this.isSilenceDetectionActive = enable
		if (enable && this.isRecording() && this.isAnalyserSetup) {
			// Start monitoring if we're already recording
			this.startAudioMonitoring()
		}
		if (!enable && this.silenceTimer) {
			clearTimeout(this.silenceTimer)
			this.silenceTimer = null
		}
	}

	// ULTIMATE: Get current audio level (0-1) for UI visualization
	getAudioLevel(): number {
		return this.currentAudioLevel
	}

	// ULTIMATE: Get peak audio level (0-1) with decay
	getPeakLevel(): number {
		return this.peakAudioLevel
	}

	// ULTIMATE: Get audio level history for waveform visualization
	getAudioLevelHistory(): number[] {
		return [...this.audioLevelHistory]
	}

	// ULTIMATE: Get frequency data for spectrum visualization
	getFrequencyData(): Uint8Array | null {
		if (!this.analyser || !this.dataArray) return null
		this.analyser.getByteFrequencyData(this.dataArray)
		return new Uint8Array(this.dataArray)
	}

	// ULTIMATE: Get time domain data for waveform visualization
	getTimeDomainData(): Uint8Array | null {
		if (!this.analyser) return null
		const timeDomainData = new Uint8Array(this.analyser.frequencyBinCount)
		this.analyser.getByteTimeDomainData(timeDomainData)
		return timeDomainData
	}

	// ULTIMATE: Update configuration
	updateConfig(config: Partial<AudioRecorderConfig>) {
		this.config = { ...this.config, ...config }
		if (this.analyser) {
			if (config.smoothingTimeConstant !== undefined) {
				this.analyser.smoothingTimeConstant = config.smoothingTimeConstant
			}
		}
	}

	async stopRecording(): Promise<Blob | null> {
		if (!this.mediaRecorder || this.mediaRecorder.state !== 'recording') {
			return null
		}

		return new Promise((resolve) => {
			this.mediaRecorder!.onstop = () => {
				const audioBlob = new Blob(this.audioChunks, {
					type: this.mediaRecorder!.mimeType,
				})

				// Clean up media stream
				if (this.stream) {
					this.stream.getTracks().forEach((track) => track.stop())
					this.stream = null
				}

				// Clean up audio context
				if (this.audioContext) {
					void this.audioContext.close()
					this.audioContext = null
					this.analyser = null
				}

				// Clear silence timer
				if (this.silenceTimer) {
					clearTimeout(this.silenceTimer)
					this.silenceTimer = null
				}

				// ULTIMATE: Reset all audio level state
				this.isSilenceDetectionActive = false
				this.isAnalyserSetup = false
				this.dataArray = null
				this.currentAudioLevel = 0
				this.peakAudioLevel = 0
				this.audioLevelHistory = []

				resolve(audioBlob)
			}

			this.mediaRecorder!.stop()
		})
	}

	isRecording(): boolean {
		return this.mediaRecorder?.state === 'recording'
	}

	// ULTIMATE: Check if audio analyser is ready
	isAnalyserReady(): boolean {
		return this.isAnalyserSetup && this.analyser !== null
	}

	// ULTIMATE: Get the MIME type being used
	getMimeType(): string | null {
		return this.mediaRecorder?.mimeType ?? null
	}
}

// Singleton instance with optional configuration
let audioRecorder: AudioRecorder | null = null
let currentConfig: Partial<AudioRecorderConfig> = {}

export function getAudioRecorder(
	config?: Partial<AudioRecorderConfig>,
): AudioRecorder {
	// If config changed, create new instance
	if (config && JSON.stringify(config) !== JSON.stringify(currentConfig)) {
		audioRecorder = new AudioRecorder(config)
		currentConfig = config
	}

	if (!audioRecorder) {
		audioRecorder = new AudioRecorder(config)
		currentConfig = config || {}
	}

	return audioRecorder
}

// ULTIMATE: Create a fresh recorder instance (useful for testing or isolation)
export function createAudioRecorder(
	config?: Partial<AudioRecorderConfig>,
): AudioRecorder {
	return new AudioRecorder(config)
}

// ULTIMATE: Audio level data type for React components
export interface AudioLevelData {
	level: number // Current audio level (0-1)
	peak: number // Peak audio level with decay (0-1)
	history: number[] // Recent level history for waveform
	isRecording: boolean
}
